import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import IosShareIcon from '@mui/icons-material/IosShare';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AndroidIcon from '@mui/icons-material/Android';
import LaptopIcon from '@mui/icons-material/Laptop';
import BackgroundContainer from '../components/BackgroundContainer';
import BaseFormContainer from '../components/BaseFormContainer';
import BaseHeader from '../components/BaseHeader';
import BaseButton from '../components/BaseButton';
import {
    getIOSVersion,
    isChromeIOS,
    isFirefoxIOS,
    isSamsungBrowser,
    isFirefoxAndroid,
    isInAppBrowser,
    isIPad,
    isIPhone,
    isAndroid,
} from '../utils/deviceDetect';

const ScrollableContent = styled(Box)(({ theme }) => ({
    overflowY: 'auto',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255, 191, 229, 0.4)', borderRadius: '8px' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' },
}));

const DeviceChip = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 16px',
    borderRadius: '50px',
    background: 'rgba(255, 191, 229, 0.08)',
    border: '1px solid rgba(255, 191, 229, 0.2)',
    color: '#ffbfe5',
    fontFamily: 'Playfair Display, serif',
    fontSize: '0.9rem',
    margin: '0 auto 20px',
});

const StepRow = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    padding: '14px 0',
    borderBottom: '1px solid rgba(255, 191, 229, 0.1)',
    '&:last-of-type': { borderBottom: 'none' },
});

const StepIconWrap = styled(Box)({
    flexShrink: 0,
    width: '38px',
    height: '38px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 191, 229, 0.1)',
    color: '#ffbfe5',
});

const StepText = styled(Typography)({
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '1.1rem',
    color: '#e8d8e2',
    lineHeight: 1.5,
    paddingTop: '6px',
});

const NoteBox = styled(Box)({
    marginTop: '18px',
    padding: '12px 16px',
    borderRadius: '12px',
    background: 'rgba(255, 191, 229, 0.06)',
    color: '#c2b8c2',
    fontFamily: 'Cormorant Garamond, serif',
    fontSize: '0.95rem',
    lineHeight: 1.4,
});

const APP_URL = 'https://deepergm.com/';

// Builds the { deviceLabel, deviceIcon, steps, note } describing exactly how
// to install on the device currently reading this page. Mirrors the same
// browser/OS branches as components/InstallGuide.js (the compact popover),
// just expanded into a full step list instead of one summary sentence.
const useInstallGuideContent = () => useMemo(() => {
    if (isInAppBrowser()) {
        return {
            deviceLabel: 'In-app browser',
            deviceIcon: <OpenInBrowserIcon fontSize="small" />,
            steps: [
                { icon: <OpenInBrowserIcon />, text: "This in-app browser can't install the game directly. Open this page in Safari or Chrome instead." },
                { icon: <ContentCopyIcon />, text: "No 'open in browser' option? Copy the link below and paste it into Safari or Chrome." },
            ],
            showCopyLink: true,
        };
    }

    if (isIPhone()) {
        if (isChromeIOS()) {
            // iOS forces every browser through the same native Share Sheet for
            // "Add to Home Screen" — Chrome can't put it in its own ⋮ menu,
            // it can only offer a route into that same Share Sheet.
            return {
                deviceLabel: 'iPhone · Chrome',
                deviceIcon: <PhoneIphoneIcon fontSize="small" />,
                steps: [
                    { icon: <IosShareIcon />, text: "Tap the Share icon (square with an arrow ↑) in Chrome's toolbar" },
                    { icon: <AddToHomeScreenIcon />, text: "Scroll down in the share menu and select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ],
                note: "Don't see a Share icon directly? Tap Chrome's ⋯ menu and choose 'Share...' first — it opens the same menu.",
            };
        }
        if (isFirefoxIOS()) {
            return {
                deviceLabel: 'iPhone · Firefox',
                deviceIcon: <PhoneIphoneIcon fontSize="small" />,
                steps: [
                    { icon: <IosShareIcon />, text: "Tap the Share icon in Firefox's toolbar" },
                    { icon: <AddToHomeScreenIcon />, text: "Select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ],
            };
        }
        // Safari — Share has lived in the bottom toolbar since the first
        // iPhone, but iOS 15+ hides it a tap deeper behind a compact address
        // bar, and users can toggle the layout in Settings — so we show both.
        const iosVersion = getIOSVersion();
        const classicLayout = iosVersion !== null && iosVersion < 15;
        return {
            deviceLabel: 'iPhone · Safari',
            deviceIcon: <PhoneIphoneIcon fontSize="small" />,
            steps: classicLayout
                ? [
                    { icon: <IosShareIcon />, text: "Tap the Share icon in the bottom toolbar" },
                    { icon: <AddToHomeScreenIcon />, text: "Select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ]
                : [
                    { icon: <IosShareIcon />, text: "Look at the bottom of the screen — if you see 5 icons in a row, tap the Share icon (square with an arrow ↑)" },
                    { icon: <MoreVertIcon />, text: "See a compact address bar instead? Tap the small icon at its edge first" },
                    { icon: <AddToHomeScreenIcon />, text: "Select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ],
            note: "Safari's layout changed in iOS 15 and can be switched back in Settings → Safari → Tabs, so your exact screen may vary slightly from these steps.",
        };
    }

    if (isIPad()) {
        const chromeOnIPad = isChromeIOS();
        return {
            deviceLabel: chromeOnIPad ? 'iPad · Chrome' : 'iPad · Safari',
            deviceIcon: <PhoneIphoneIcon fontSize="small" />,
            steps: chromeOnIPad
                ? [
                    { icon: <IosShareIcon />, text: "Tap the Share icon (square with an arrow ↑) in Chrome's toolbar" },
                    { icon: <AddToHomeScreenIcon />, text: "Scroll down in the share menu and select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ]
                : [
                    { icon: <IosShareIcon />, text: "Tap the Share icon near the top of the screen" },
                    { icon: <AddToHomeScreenIcon />, text: "Select 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm by tapping 'Add'" },
                ],
            note: chromeOnIPad ? "Don't see a Share icon directly? Tap Chrome's ⋯ menu and choose 'Share...' first — it opens the same menu." : undefined,
        };
    }

    if (isAndroid()) {
        if (isSamsungBrowser()) {
            return {
                deviceLabel: 'Android · Samsung Internet',
                deviceIcon: <AndroidIcon fontSize="small" />,
                steps: [
                    { icon: <MoreVertIcon />, text: "Tap ⋮ near the top of the screen" },
                    { icon: <AddToHomeScreenIcon />, text: "Tap 'Add page to' → 'Home screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm to finish" },
                ],
            };
        }
        if (isFirefoxAndroid()) {
            return {
                deviceLabel: 'Android · Firefox',
                deviceIcon: <AndroidIcon fontSize="small" />,
                steps: [
                    { icon: <MoreVertIcon />, text: "Tap ⋮ in the bottom toolbar" },
                    { icon: <AddToHomeScreenIcon />, text: "Select 'Install' or 'Add to Home Screen'" },
                    { icon: <CheckCircleIcon />, text: "Confirm to finish" },
                ],
            };
        }
        return {
            deviceLabel: 'Android · Chrome',
            deviceIcon: <AndroidIcon fontSize="small" />,
            steps: [
                { icon: <MoreVertIcon />, text: "Tap ⋮ near the top of the screen" },
                { icon: <AddToHomeScreenIcon />, text: "Select 'Install app' or 'Add to Home Screen'" },
                { icon: <CheckCircleIcon />, text: "Confirm to finish" },
            ],
            note: "Some Chrome versions also show an install icon directly in the address bar as a shortcut.",
        };
    }

    // Desktop / unrecognized device
    return {
        deviceLabel: 'Desktop',
        deviceIcon: <LaptopIcon fontSize="small" />,
        steps: [
            { icon: <PhoneIphoneIcon />, text: "This game installs from a phone's browser, not a desktop one." },
            { icon: <ContentCopyIcon />, text: "Copy the link below and open it on your phone to install it there." },
        ],
        showCopyLink: true,
    };
}, []);

const DownloadPage = () => {
    const navigate = useNavigate();
    const guide = useInstallGuideContent();
    const [copied, setCopied] = useState(false);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(APP_URL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (e) {
            // clipboard API unavailable — nothing more we can do silently
        }
    };

    return (
        <BackgroundContainer>
            <BaseFormContainer sx={{ width: '85%', flex: 1, maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <BaseHeader sx={{
                    fontSize: '2.6rem',
                    textAlign: 'center',
                    paddingTop: '10px',
                    filter: 'drop-shadow(0 0 3px rgba(177, 39, 135, 0.36))',
                    '@media (max-width: 363px)': { fontSize: '2.2rem' },
                }}>
                    Install Deeper
                </BaseHeader>

                <DeviceChip>
                    {guide.deviceIcon}
                    {guide.deviceLabel}
                </DeviceChip>

                <ScrollableContent>
                    {guide.steps.map((step, index) => (
                        <StepRow key={index}>
                            <StepIconWrap>{step.icon}</StepIconWrap>
                            <StepText>{step.text}</StepText>
                        </StepRow>
                    ))}

                    {guide.note && <NoteBox>{guide.note}</NoteBox>}

                    {guide.showCopyLink && (
                        <NoteBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                            <Typography sx={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '0.95rem', wordBreak: 'break-all' }}>
                                {APP_URL}
                            </Typography>
                            <Box
                                onClick={handleCopyLink}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                                    color: '#ffbfe5', fontFamily: 'Playfair Display, serif', fontSize: '0.85rem',
                                    padding: '6px 12px', borderRadius: '50px', border: '1px solid rgba(255, 191, 229, 0.3)',
                                    flexShrink: 0,
                                }}
                            >
                                <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                                {copied ? 'Copied!' : 'Copy Link'}
                            </Box>
                        </NoteBox>
                    )}
                </ScrollableContent>

                <BaseButton sx={{ marginTop: 3, alignSelf: 'center' }} onClick={() => navigate('/')}>Back</BaseButton>
            </BaseFormContainer>
        </BackgroundContainer>
    );
};

export default DownloadPage;
